import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import * as Avatar from '@radix-ui/react-avatar';
import { AdminPortal } from '@frontegg/react';
import { useAppAuth } from '@/hooks/useAppAuth';
import { useUserRoles } from '@/hooks/useUserRoles';
import { Moon, Sun, ChevronRight, Home, Bot, Database, BarChart3, CheckCircle2, Undo2, Code, Gauge, BookOpen, Library, MessageSquare, Settings, Users, Shield, AlertTriangle, Lightbulb, Hammer, Layers, FileText, CircleDot, ClipboardList, PackageOpen, Workflow, LifeBuoy, Target } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTheme } from '@/contexts/ThemeContext';
import { useFeatureFlag, FeatureFlag } from '@/services/featureFlag.service';
import { useRequestBadgeCount, usePendingKbCount } from '@/hooks/useNavBadges';
import { useLogout } from '@/hooks/useLogout';
import { useBrandLogoInfo } from '@/hooks/useBrandLogo';
import { RequireRole } from '@/components/RequireRole';
import { BuildAllModal } from '@/components/BuildAllModal';
import { PublishChangesDialog } from '@/components/PublishChangesDialog';
import { useIsInDraftMode, useDraftModeToggle } from '@/hooks/useDraftMode';
import { useSourceRepo } from '@/hooks/useGit';
import ApiService from '@/services/api.service';
import AuthService from '@/services/auth.service';
import BackgroundService from '@/services/bg.service';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuBadge,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import TenantSwitcher from './TenantSwitcher';

interface MenuItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
  children?: MenuItem[];
  onClick?: () => void;
  badge?: number;
}

export default function AppSidebar() {
  const { user, isAuthenticated } = useAppAuth();
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  const authService = AuthService.getInstance();

  const finishLogout = useLogout();
  const location = useLocation();

  // Close mobile sidebar sheet when route changes
  useEffect(() => {
    setOpenMobile(false);
  }, [location.pathname]);

  const { theme, toggleTheme } = useTheme();
  const isInDraftMode = useIsInDraftMode();
  const onToggleDraftMode = useDraftModeToggle();
  const queryClient = useQueryClient();
  const isTemplateManagementEnabled = useFeatureFlag('template-management', false);
  const isAiAssistantEnabled = useFeatureFlag(FeatureFlag.AI_ASSISTANT, false);
  const isAnalysisSectionEnabled = useFeatureFlag('access-analysis-section', false);
  const isSemanticLayerEnabled = useFeatureFlag('access-semantic-section', true);
  const isDocsLinkHidden = useFeatureFlag('hide-docs-link', false);
  const useCasesEnabled = useFeatureFlag(FeatureFlag.ALLOW_USECASE_MANAGEMENT, false);
  const viewTasksEnabled = useFeatureFlag(FeatureFlag.VIEW_TASKS, false);
  const viewGapsEnabled = useFeatureFlag(FeatureFlag.VIEW_SEMANTIC_GAPS, false);
  const isGoalsEnabled = useFeatureFlag(FeatureFlag.ENABLE_GOALS, false);
  const [reverting, setReverting] = useState(false);
  // Reverting in Pliable is refused when the customer owns the repository: git
  // holds the history there, so a revert is `git checkout` plus a force-push to
  // the Develop branch, which the import then brings back in. Doing it here would
  // leave the branch — the thing their other tooling reads — still holding the
  // work that was thrown away.
  const { data: sourceRepo } = useSourceRepo();
  const revertsThroughGit = sourceRepo?.auth_mode === 'CUSTOMER_APP';
  const [buildAllOpen, setBuildAllOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);

  // Track expanded sections
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());

  // Admin / Builder / ReadOnly vs ReadOnly_Partner. `canViewDataManagement`
  // gates everything tenant-internal, Requests included.
  const { isAdmin, isAdminOrBuilder, canViewDataManagement } = useUserRoles();

  // Unread requests drive the nav badge; a reply can land while the user is on any page.
  // Shared with the mobile tab bar and home screen — see useNavBadges.
  const requestBadgeCount = useRequestBadgeCount(canViewDataManagement);
  const pendingKbCount = usePendingKbCount(useCasesEnabled && canViewDataManagement);

  // Top navigation items
  const topItems: MenuItem[] = [
    { label: 'Home', path: '/', icon: <Home className="h-4 w-4" /> },
    ...(isAiAssistantEnabled ? [{ label: 'Ellie', path: '/ellie', icon: <MessageSquare className="h-4 w-4" /> }] : []),
  ];

  // Main section items
  const mainItems: MenuItem[] = [
    ...(canViewDataManagement ? [{ label: 'Agents', path: '/agents', icon: <Bot className="h-4 w-4" /> }] : []),
    ...(isAnalysisSectionEnabled ? [{ label: 'Analytics', path: '/analytics', icon: <BarChart3 className="h-4 w-4" /> }] : []),
    // Goals are withheld from ReadOnly_Partner (the goals API denies them), so
    // don't offer a nav item that only leads to a 403.
    ...(isAnalysisSectionEnabled && isGoalsEnabled && canViewDataManagement ? [{ label: 'Goals', path: '/goals', icon: <Target className="h-4 w-4" /> }] : []),
    ...(canViewDataManagement ? [{ label: 'Data Sources', path: '/sources', icon: <Database className="h-4 w-4" /> }] : []),
    ...(canViewDataManagement && !useCasesEnabled ? [{ label: 'Context', path: '/data/context', icon: <Lightbulb className="h-4 w-4" /> }] : []),
    ...(useCasesEnabled && canViewDataManagement ? [{ label: 'Knowledge Base', path: '/knowledge-base', icon: <Library className="h-4 w-4" />, badge: pendingKbCount }] : []),
    ...(canViewDataManagement ? [{ label: 'Artifacts', path: '/artifacts', icon: <FileText className="h-4 w-4" /> }] : []),
    ...(canViewDataManagement ? [{ label: 'Requests', path: '/requests', icon: <LifeBuoy className="h-4 w-4" />, badge: requestBadgeCount }] : []),
  ];

  // Develop mode items
  const developItems: MenuItem[] = [
    // The Data Model covers models, entities, metrics and dimensions — models have no entry
    // of their own, since its quick nav is where they're reached from. Without the semantic
    // layer there'd be no way into the models at all, so that case keeps its own entry.
    ...(isSemanticLayerEnabled
      ? [{ label: 'Data Model', path: '/data-model', icon: <Workflow className="h-4 w-4" /> }]
      : [{ label: 'Models', path: '/data-model/models', icon: <Code className="h-4 w-4" /> }]),
    // Gaps are internal telemetry now — in-app intake is Requests. The page is
    // off unless someone deliberately turns it on for a tenant.
    ...(viewGapsEnabled ? [{ label: 'Gaps', path: '/develop/gaps', icon: <CircleDot className="h-4 w-4" /> }] : []),
    ...(viewTasksEnabled ? [{ label: 'Tasks', path: '/develop/tasks', icon: <ClipboardList className="h-4 w-4" /> }] : []),
    ...(viewTasksEnabled ? [{ label: 'Parked', path: '/develop/tasks/parked', icon: <PackageOpen className="h-4 w-4" /> }] : []),
    ...(isTemplateManagementEnabled && isAdmin ? [{ label: 'Templates', path: '/templates/install', icon: <Layers className="h-4 w-4" /> }] : []),
    ...(isAdmin ? [{ label: 'Usage Analytics', path: '/usage-analytics', icon: <Gauge className="h-4 w-4" /> }] : []),
    { label: 'Danger Zone', path: '/develop/danger-zone', icon: <AlertTriangle className="h-4 w-4" /> },
  ];

  // The Data Model stays reachable in production mode, where it renders read-only; Usage
  // Analytics is mode-independent. Everything else in the develop menu is draft-mode only.
  const PRODUCTION_MODE_PATHS = ['/data-model', '/data-model/models', '/usage-analytics'];
  const visibleDevelopItems = isInDraftMode
    ? developItems
    : developItems.filter((item) => PRODUCTION_MODE_PATHS.includes(item.path));

  const showAdminPortal = () => {
    AdminPortal.show();
  };

  // Bottom items (pinned above user menu)
  const bottomItems: MenuItem[] = [
    ...(isAdmin ? [
      { label: 'Settings', path: '/settings', icon: <Settings className="h-4 w-4" /> },
      { label: 'Security & Governance', path: '/security', icon: <Shield className="h-4 w-4" /> },
      ...(!authService.oidcEnabled ? [{ label: 'User Management', path: '#', icon: <Users className="h-4 w-4" />, onClick: showAdminPortal }] : []),
    ] : []),
    ...(!isDocsLinkHidden && canViewDataManagement ? [{ label: 'Documentation', path: '/docs', icon: <BookOpen className="h-4 w-4" /> }] : []),
  ];

  // Auto-expand sections if we're on their routes
  useEffect(() => {
    const allItems = [...topItems, ...mainItems, ...developItems, ...bottomItems];
    const newExpanded = new Set<string>();
    allItems.forEach((item) => {
      if (item.children && location.pathname.startsWith(item.path)) {
        newExpanded.add(item.path);
      }
    });
    setExpandedSections(newExpanded);
  }, [location.pathname]);

  const onToggleExpand = (path: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  const handlePublishChanges = () => {
    setPublishOpen(true);
  };

  const handleRevertChanges = async () => {
    if (!confirm('Are you sure you want to revert your changes? We will copy everything in production into your develop mode.')) {
      return;
    }

    setReverting(true);
    try {
      const resp = await ApiService.getInstance().request('POST', '/draftversions/undo') as { job_id: string };
      await BackgroundService.getInstance().waitForJob(resp.job_id);
      queryClient.invalidateQueries();
    } catch (error) {
      console.error('Failed to revert changes:', error);
    } finally {
      setReverting(false);
    }
  };

  const handleToggleDevelopMode = async () => {
    await onToggleDraftMode('sidebar');
  };

  const handleBuildAll = () => {
    setBuildAllOpen(true);
  };

  // Only show sidebar if authenticated
  if (!isAuthenticated || !user) {
    return null;
  }

  const userDisplayName = user.name || user.email || 'User';
  const userEmail = user.email || '';
  const userAvatar = user.profilePictureUrl || undefined;

  // The sidebar is dark in both themes, so the white logo is the readable one
  // either way. Shared with the mobile home screen — see useBrandLogo.
  const { src: logoToUse, isCustom: hasCustomLogo } = useBrandLogoInfo({ onDarkSurface: true });

  const isItemActive = (item: MenuItem) => {
    if (item.path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  };

  const renderMenuItem = (item: MenuItem) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedSections.has(item.path);
    const isActive = isItemActive(item);
    if (hasChildren) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton
            onClick={() => onToggleExpand(item.path)}
            isActive={isActive}
            tooltip={item.label}
          >
            {item.icon}
            <span>{item.label}</span>
            <ChevronRight
              className={`ml-auto h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
            />
          </SidebarMenuButton>
          {isExpanded && (
            <SidebarMenuSub>
              {item.children!.map((child) => {
                const childIsActive = location.pathname === child.path || location.pathname.startsWith(child.path + '/');
                return (
                  <SidebarMenuSubItem key={child.path}>
                    <SidebarMenuSubButton asChild isActive={childIsActive}>
                      <Link to={child.path}>{child.label}</Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                );
              })}
            </SidebarMenuSub>
          )}
        </SidebarMenuItem>
      );
    }

    // If item has onClick, render as a button instead of a link
    if (item.onClick) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton onClick={item.onClick} tooltip={item.label}>
            {item.icon}
            <span>{item.label}</span>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }

    return (
      <SidebarMenuItem key={item.path}>
        <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
          <Link to={item.path}>
            {item.icon}
            <span>{item.label}</span>
          </Link>
        </SidebarMenuButton>
        {item.badge != null && item.badge > 0 && (
          <SidebarMenuBadge className="bg-sidebar-active/20 text-sidebar-active-foreground">{item.badge}</SidebarMenuBadge>
        )}
      </SidebarMenuItem>
    );
  };

  return (
    // "icon" rather than the default "offcanvas": collapsing leaves a rail of
    // icons instead of removing navigation altogether. Every menu button below
    // already passes a `tooltip`, which is what labels that rail on hover.
    <Sidebar collapsible="icon">
      {/* The logo and tenant picker are the only wide things in here; both are
          hidden at icon width rather than squashed into an unreadable sliver. */}
      <SidebarHeader className="p-4 group-data-[collapsible=icon]:hidden">
        <Link to="/" className="flex items-center">
          {/* Tenant logos come in arbitrary aspect ratios; a fixed h-8 shrinks wide ones
              to a sliver, so give them a taller box bounded by the sidebar width. */}
          <img
            src={logoToUse}
            alt="Pliable"
            className={hasCustomLogo ? 'h-auto max-h-20 w-auto max-w-full object-contain object-left' : 'h-8'}
          />
        </Link>
        <div data-testid="tenant-picker">
          <TenantSwitcher />
        </div>
      </SidebarHeader>

      <SidebarContent>
        {/* Top Section: Home and Ellie */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {topItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="mx-4 border-t border-sidebar-border group-data-[collapsible=icon]:mx-2" />

        {/* Main Section: Agents, Analytics, Glossary, Data Sources, Context */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Develop/Production Mode Section */}
        <RequireRole role="Builder">
          <div className="mx-4 border-t border-sidebar-border" />
          <SidebarGroup>
            <SidebarGroupContent>
              <div className="px-2 py-2 group-data-[collapsible=icon]:px-0">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className="w-full text-left p-2 rounded-lg hover:bg-sidebar-accent/5 focus:outline-none focus:ring-2 focus:ring-sidebar-ring transition-colors group-data-[collapsible=icon]:flex group-data-[collapsible=icon]:justify-center"
                      // The label is the only thing identifying which mode you
                      // are in, and the rail has no room for it — so it becomes
                      // the hover title instead of disappearing entirely.
                      title={isInDraftMode ? 'Develop Mode — making changes' : 'Production Mode — read only'}
                    >
                      <div className="flex items-center gap-2">
                        {isInDraftMode ? (
                          <Hammer className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
                        ) : (
                          <Shield className="h-4 w-4 flex-shrink-0 text-sidebar-foreground/70" />
                        )}
                        <div className="flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                          <div className="text-sm font-medium text-sidebar-foreground">
                            {isInDraftMode ? 'Develop Mode' : 'Production Mode'}
                          </div>
                          <div className="text-xs text-sidebar-foreground/60">
                            {isInDraftMode ? 'Making changes' : 'Read only'}
                          </div>
                        </div>
                        <ChevronRight className="h-4 w-4 text-sidebar-foreground/60 group-data-[collapsible=icon]:hidden" />
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent side="right" align="start" className="w-56 p-2">
                    <div className="space-y-3">
                      <div className="space-y-1">
                        {isInDraftMode ? (
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handleToggleDevelopMode}
                          >
                            Enter Production Mode
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handleToggleDevelopMode}
                          >
                            Enter Develop Mode
                          </Button>
                        )}
                      </div>
                      {isInDraftMode && (
                        <div className="border-t pt-2 space-y-1">
                          <Button
                            variant="ghost"
                            className="w-full justify-start"
                            onClick={handlePublishChanges}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-2" />
                            Publish Changes
                          </Button>
                          {revertsThroughGit ? (
                            <p className="px-3 py-2 text-xs text-muted-foreground">
                              To discard Develop changes, force-push the commit you
                              want to the Develop branch. Pliable will import it.
                            </p>
                          ) : (
                            <Button
                              variant="ghost"
                              className="w-full justify-start"
                              onClick={handleRevertChanges}
                              disabled={reverting}
                            >
                              <Undo2 className="h-4 w-4 mr-2" />
                              {reverting ? 'Reverting...' : 'Revert Changes'}
                            </Button>
                          )}
                        </div>
                      )}
                      <div className="border-t pt-2 space-y-1">
                        <Button
                          variant="ghost"
                          className="w-full justify-start"
                          onClick={handleBuildAll}
                        >
                          <Hammer className="h-4 w-4 mr-2" />
                          Build All
                        </Button>
                      </div>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Develop menu items (a read-only subset in production mode) */}
              <SidebarMenu>
                {visibleDevelopItems.map(renderMenuItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </RequireRole>

        {/* Spacer to push bottom items down */}
        <div className="flex-1" />

        {/* Bottom Section: Settings, User Management */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {bottomItems.map(renderMenuItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center w-full p-2 rounded-lg hover:bg-sidebar-accent/5 focus:outline-none focus:ring-2 focus:ring-sidebar-ring transition-colors group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-1"
              title={`${userDisplayName} (${userEmail})`}
            >
              {/* Shrinks on the rail so the avatar fits the icon width without
                  forcing the sidebar wider than its own variable. */}
              <Avatar.Root className="inline-flex items-center justify-center align-middle overflow-hidden select-none w-10 h-10 rounded-full bg-sidebar-accent/10 flex-shrink-0 group-data-[collapsible=icon]:w-8 group-data-[collapsible=icon]:h-8">
                <Avatar.Image
                  className="w-full h-full object-cover"
                  src={userAvatar}
                  alt={userDisplayName}
                />
                <Avatar.Fallback className="w-full h-full flex items-center justify-center bg-sidebar-accent/10 text-sidebar-foreground text-sm font-medium">
                  {userDisplayName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2)}
                </Avatar.Fallback>
              </Avatar.Root>
              <div className="ml-3 text-left flex-1 min-w-0 group-data-[collapsible=icon]:hidden">
                <div className="text-sm font-medium text-sidebar-foreground truncate">
                  {userDisplayName}
                </div>
                <div className="text-xs text-sidebar-foreground/70 truncate">
                  {userEmail}
                </div>
              </div>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent
            className="min-w-[220px]"
            sideOffset={5}
            align="end"
          >
            <DropdownMenuItem
              onSelect={toggleTheme}
              className="flex items-center"
            >
              {theme === 'dark' ? (
                <Sun className="mr-2 h-4 w-4" />
              ) : (
                <Moon className="mr-2 h-4 w-4" />
              )}
              <span>Toggle {theme === 'dark' ? 'light' : 'dark'} mode</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {!authService.oidcEnabled && (
              <>
                <DropdownMenuItem onSelect={showAdminPortal}>
                  Manage Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onSelect={() => finishLogout()}
            >
              Log Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarFooter>

      <BuildAllModal open={buildAllOpen} onOpenChange={setBuildAllOpen} />
      <PublishChangesDialog open={publishOpen} onOpenChange={setPublishOpen} />
    </Sidebar>
  );
}
